<template>
  <div
    v-if="showPriceTable"
    :class="[externalPage ? 'not-loged d-flex flex-column' : 'select-card d-flex flex-column', 'modern-price-table']"
  >
    <div class="price-table-header">
      <div class="header-main">
        <h3 class="header-title">
          {{ $t('price_table.title') }}
        </h3>
        <div
          v-if="plans && plans.point_name"
          class="point-name"
        >
          {{ $t('price_table.title_2') }}: <strong>{{ plans.point_name }}</strong>
        </div>
      </div>
      <ModalCloseButton @close="closePriceTable" />
    </div>
    <div class="header-divider"></div>

    <div
      v-if="plans"
      class="plans-container"
    >
      <p class="text-center text-muted small mb-3">
        {{ $t('price_table.text') }}
      </p>

      <div class="status-card shadow-sm mb-3">
        <div
          v-if="campaign"
          class="free-time-bar mb-3"
        >
          <font-awesome-icon
            icon="fa-solid fa-bolt"
            class="free-icon"
          />
          <span class="free-text">
            <span v-text="campaignFreeTimeText()"></span>
            <strong
              style="color: #16a34a"
              v-text="$t('price_table.free')"
            ></strong>
          </span>
        </div>

        <div class="info-row align-items-start flex-column gap-3">
          <div
            v-if="planHourly"
            class="info-item w-100 justify-content-start"
          >
            <font-awesome-icon
              icon="fa-solid fa-clock"
              class="info-icon"
            />
            <div class="info-content">
              <span
                class="info-value text-dark fw-bold"
                v-text="planText(planHourly)"
              ></span>
            </div>
          </div>

          <div
            v-if="planDaily"
            class="info-item w-100 justify-content-start"
          >
            <font-awesome-icon
              icon="fa-solid fa-calendar-day"
              class="info-icon"
            />
            <div class="info-content">
              <span
                class="info-value text-dark fw-bold"
                v-text="planText(planDaily)"
              ></span>
            </div>
          </div>
        </div>
      </div>

      <div class="status-card shadow-sm bg-light-card mb-3">
        <div class="info-row flex-column align-items-start gap-2">
          <div class="info-content w-100">
            <span class="info-label">{{ $t('price_table.text_2') }}</span>
            <span class="info-value d-block mt-1 fw-bold text-dark">
              {{ formatPrice(planDaily.amount) }}
            </span>
          </div>
          <div class="info-divider horizontal w-100 my-1"></div>
          <div class="info-content w-100">
            <span class="info-label">{{ $t('price_table.text_3') }}</span>
            <span class="info-value d-block mt-1 text-orange">
              {{ formatPrice(maxPlan.amount) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-show="!internalPointId"
      class="token-section"
    >
      <p class="token-help-text">
        {{ $t('price_table.help_text_find_token') }}
      </p>
      <form
        class="token-form"
        @submit.prevent="inputToken"
      >
        <div class="token-input-group">
          <input
            id="input-token"
            ref="inputToken"
            v-model.trim="internalToken"
            type="text"
            class="token-input-field"
            maxlength="4"
            name="token_mini"
            :placeholder="$t('price_table.insert_token')"
          >
          <BaseButton
            type="submit"
            variant="primary"
            size="sm"
            class="token-submit-btn"
            :disabled="loading"
          >
            <font-awesome-icon
              v-if="!loading"
              icon="fa-solid fa-magnifying-glass"
              class="me-1"
            />
            <b-spinner
              v-else
              small
              class="me-1"
            />
            {{ $t('price_table.consult') }}
          </BaseButton>
        </div>
        <p
          v-if="message"
          :class="['token-message', messageClass]"
        >
          {{ message }}
        </p>
      </form>
    </div>

    <div class="container mt-2">
      <ul class="small text-muted observations-list mb-0">
        <li>
          <font-awesome-icon
            icon="fa-solid fa-circle-info"
            class="me-1 opacity-50"
          />
          * {{ $t('price_table.obs1') }}.
        </li>
        <li>
          <font-awesome-icon
            icon="fa-solid fa-circle-info"
            class="me-1 opacity-50"
          />
          * {{ $t('price_table.obs2') }}.
        </li>
        <li>
          <font-awesome-icon
            icon="fa-solid fa-circle-info"
            class="me-1 opacity-50"
          />
          * {{ $t('price_table.obs3') }}.
        </li>
        <li v-if="campaign">
          <font-awesome-icon
            icon="fa-solid fa-circle-info"
            class="me-1 opacity-50"
          />
          * {{ $t('price_table.obs4') }}.
        </li>
        <li v-if="pix_security_deposit">
          <font-awesome-icon
            icon="fa-solid fa-circle-info"
            class="me-1 opacity-50"
          />
          * {{ $t('price_table.obs5', { deposit: formatPrice(pix_security_deposit) }) }}.
        </li>
      </ul>
    </div>

    <div class="footer-divider"></div>
    <div class="footer-actions mb-1">
      <div class="d-flex justify-content-end gap-2 w-100 flex-wrap">
        <BaseButton
          v-if="showFooterBackButton"
          variant="secondary"
          unstyled
          class="action-btn-modern flex-grow-1"
          @click="goBack"
        >
          <font-awesome-icon
            icon="fa-solid fa-chevron-left"
            class="me-1"
          />
          {{ $t('price_table.back') }}
        </BaseButton>
        <BaseButton
          v-if="internalPointId && allowEdit"
          id="new-consult-price-table"
          variant="secondary"
          unstyled
          class="action-btn-modern flex-grow-1"
          @click="clearToken"
        >
          <font-awesome-icon
            icon="fa-solid fa-rotate-right"
            class="me-1"
          />
          {{ $t('price_table.new_consult') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>
<script>
  import '@/main.scss'
  import API from '@/services/api'
  import getPointByToken from '@/services/getPointName'
  import getPlans from '@/views/home/service/getPlans'
  import getCustomerPlans from '@/views/home/service/getCustomerPlans'
  import BaseButton from '@/components/ui/BaseButton.vue'
  import ModalCloseButton from '@/components/ui/ModalCloseButton.vue'
  import { formatPrice } from '@/utils/formatPrice'
  import { getPlanSeconds } from '@/utils/planTime'

  export default {
    name: 'PriceTable',
    components: {
      BaseButton,
      ModalCloseButton
    },
    props: {
      externalPage: {
        type: Boolean,
        default: false
      },
      showPriceTable: {
        type: Boolean,
        default: false
      },
      pointId: {
        type: Number,
        default: null
      },
      allowEdit: {
        type: Boolean,
        default: true
      },
      token: {
        type: String,
        default: null
      }
    },
    emits: ['close', 'handleTokenSelection'],
    data() {
      return {
        plans: null,
        plan_4_hours: null,
        plan_daily: null,
        pix_price: null,
        planHourly: null,
        planDaily: null,
        maxPlan: null,
        campaign: null,
        campaignFreeTime: null,
        pix_security_deposit: null,
        price_variation: null,
        loading: false,
        internalToken: this.$props.token || null,
        message: '',
        messageClass: '',
        internalPointId: null
      }
    },
    computed: {
      showFooterBackButton() {
        return this.internalPointId && !this.pointId
      }
    },
    watch: {
      pointId: {
        handler(newVal) {
          if (newVal !== undefined) {
            this.getPointInfo()
          }
        },
        immediate: true
      }
    },
    async created() {
      this.pix_security_deposit = await this.getPixPrice()
    },
    async mounted() {
      this.getPointInfo()
    },
    methods: {
      async getPointInfo() {
        this.internalPointId = this.pointId
        if (this.internalPointId != null) {
          sessionStorage.setItem('point_id_token_selection', this.pointId)
          await this.getPlanInfo()
          return
        }
        if (!this.internalToken) {
          this.internalToken = localStorage.getItem('terminal_token')
        }
        if (this.internalToken !== null) {
          const pointResponse = await getPointByToken(this.internalToken)
          if (pointResponse.success) {
            this.internalPointId = pointResponse.id
            await this.getPlanInfo()
          }
          return
        }
      },
      async getPixPrice() {
        try {
          const response = await API.get('payment/pix-price')
          return response.data.pix_security_deposit.security_deposit
        } catch (error) {
          console.log(error)
        }
      },
      async getPlanInfo() {
        if (this.internalPointId === undefined || this.internalPointId === null) {
          return
        }

        try {
          const token = localStorage?.getItem('token') || null
          if (token) {
            this.plans = await getCustomerPlans(this.internalPointId)
          } else {
            this.plans = await getPlans(this.internalPointId, false)
          }

          if (this.plans && this.internalPointId !== undefined && this.internalPointId !== null) {
            this.planHourly = this.plans.price.find(prices => prices.internal_ref === 'four_hours')
            if (this.planHourly && this.planHourly.price_variation) {
              this.planHourly = this.applyPriceVariation(this.planHourly)
            }

            this.planDaily = this.plans.price.find(prices => prices.internal_ref === 'daily')
            if (this.planDaily && this.planDaily.price_variation) {
              this.planDaily = this.applyPriceVariation(this.planDaily)
            }

            this.maxPlan = this.plans.price.find(prices => prices.internal_ref === 'quarterly')

            if (this.plans.campaign && this.plans.campaign.free_time >= 300) {
              this.campaign = this.plans.campaign
              this.campaignFreeTime = this.campaign.free_time / 60
            }
          } else {
            this.planHourly = this.plans.on_demand.prices.find(prices => prices.internal_ref === 'four_hours')
            this.planDaily = this.plans.on_demand.prices.find(prices => prices.internal_ref === 'daily')
          }
        } catch (error) {
          console.error('Erro ao obter informações do plano:', error)
          this.message = 'Ocorreu um erro ao carregar as informações do plano. Tente novamente mais tarde.'
          this.messageClass = 'text-danger'
        }
      },
      campaignFreeTimeText() {
        const totalSeconds = Math.floor(this.campaign.free_time)
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60

        let prefix = this.$t('price_table.campaign_first_hours')
        let duration = ''

        if (hours > 0) {
          prefix = this.$t('price_table.campaign_first_hours')
          duration = `${hours}${this.$t('price_table.hours')}`
        } else if (minutes > 0) {
          prefix = this.$t('price_table.campaign_first_minutes')
          duration = `${minutes} ${this.$t('price_table.minutes_short')}`
        } else if (seconds > 0) {
          prefix = this.$t('price_table.campaign_first_minutes')
          duration = `${seconds}s`
        }

        return `${prefix} ${duration}: `
      },
      planText(planTime) {
        const value = this.formatPrice(planTime.amount)
        const planTimeSeconds = getPlanSeconds(planTime)

        const timeString = this.formatDurationFromSeconds(planTimeSeconds)

        if (planTime === this.planHourly) {
          if (this.campaign) {
            return `${this.$t('price_table.after_free', { duration: timeString })}: ${value}`
          }
          return `${this.$t('price_table.first_period', { duration: timeString })}: ${value}`
        }

        if (planTime === this.planDaily) {
          if (this.planHourly || this.campaign) {
            return `${this.$t('price_table.after_hourly')}: ${value}`
          }
          return `${this.$t('price_table.daily_only')}: ${value}`
        }

        return `${timeString}: ${value}`
      },
      formatDurationFromSeconds(totalSeconds) {
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60

        const parts = []
        if (hours > 0) parts.push(`${hours}${this.$t('price_table.hours')}`)
        if (minutes > 0) parts.push(`${minutes} ${this.$t('price_table.minutes_short')}`)
        if (seconds > 0) parts.push(`${seconds}s`)

        if (parts.length === 0) {
          return `0 ${this.$t('price_table.minutes_short')}`
        } else if (parts.length === 1) {
          return parts[0]
        } else {
          const lastPart = parts.pop()
          return parts.join(', ') + ' e ' + lastPart
        }
      },
      clearToken() {
        this.internalPointId = null
        this.internalToken = null
        this.plans = null

        this.$nextTick(() => {
          if (this.$refs.inputToken) {
            this.$refs.inputToken.focus()
          }
        })
      },

      goBack() {
        this.internalPointId = null
        this.plans = null
        this.planHourly = null
        this.planDaily = null
        this.maxPlan = null
        this.campaign = null
        this.campaignFreeTime = null
        this.message = ''
        this.messageClass = ''
        this.internalToken = localStorage.getItem('terminal_token')

        this.$nextTick(() => {
          if (this.$refs.inputToken) {
            this.$refs.inputToken.focus()
          }
        })
      },

      cleanValues() {
        this.plans = null
        this.plan_4_hours = null
        this.plan_daily = null
        this.pix_price = null
        this.planHourly = null
        this.planDaily = null
        this.maxPlan = null
        this.campaign = null
        this.campaignFreeTime = null
        this.price_variation = null
      },
      async inputToken() {
        this.cleanValues()
        this.message = ''
        this.messageClass = ''
        if (!this.internalToken || this.internalToken.trim() === '') {
          this.message = 'Por favor, insira um token válido.'
          this.messageClass = 'text-danger'
          return
        }
        try {
          this.loading = true
          const pointResponse = await getPointByToken(this.internalToken)
          if (!pointResponse.success) {
            this.message = this.$t('price_table.token_not_found')
            this.messageClass = 'text-danger'
            return
          }
          if (pointResponse.success) {
            this.internalPointId = pointResponse.id
            localStorage.setItem('terminal_token', this.internalToken)
            await this.getPlanInfo()
          } else {
            this.message = 'Token inválido ou não encontrado.'
            this.messageClass = 'text-danger'
          }
        } catch (error) {
          console.error('Erro ao consultar token:', error)
          this.message = 'Ocorreu um erro ao consultar o token. Tente novamente mais tarde.'
          this.messageClass = 'text-danger'
        } finally {
          this.loading = false
        }
      },
      closePriceTable() {
        this.$emit('close', true)
      },
      handleTokenSelection() {
        this.$emit('close', true)
        this.$emit('handleTokenSelection')
      },
      applyPriceVariation(price) {
        const variationFactor = 1 + price.price_variation / 100
        const totalCentavos = Math.round(
          (parseInt(price.amount.reais) * 100 + parseInt(price.amount.centavos)) * variationFactor
        )
        price.amount.reais = Math.floor(totalCentavos / 100)
        price.amount.centavos = totalCentavos % 100
        return price
      },
      formatPrice
    }
  }
</script>
<style scoped lang="scss">
  @use '@/main';

  .modern-price-table {
    width: 100%;
    margin: 0 auto;
    position: relative;
    padding: 24px;
  }

  .price-table-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 0;
  }

  .header-main {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .header-title {
    font-family: var(--font-heading);
    font-weight: var(--fw-semibold);
    font-size: var(--fs-lg);
    color: #424242;
    letter-spacing: var(--ls-tight);
    line-height: 1.15;
    margin: 0 0 0.35em;
  }

  .point-name {
    font-size: var(--fs-xs);
    color: #6b7280;
    line-height: 1.4;
  }

  .point-name strong {
    color: #1d1d1f;
    font-weight: var(--fw-semibold);
  }

  .header-divider {
    height: 1px;
    background-color: #e5e7eb;
    margin: 16px 0;
  }

  .footer-divider {
    height: 1px;
    background-color: #e5e7eb;
    margin: 24px 0 16px;
  }

  .status-card {
    background: white;
    border-radius: 16px;
    padding: 16px;
    border-left: 4px solid main.$color-energy-orange;
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .bg-light-card {
    background-color: #f9fafb !important;
    border-left-color: #e5e7eb;
  }

  .info-row {
    display: flex;
    padding: 4px 0;
  }

  .info-item {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .info-icon {
    font-size: var(--fs-xl);
    color: main.$color-energy-dark-gray;
    opacity: 0.7;
    width: 24px;
    text-align: center;
  }

  .info-content {
    display: flex;
    flex-direction: column;
  }

  .info-label {
    font-size: var(--fs-xs);
    color: #6b7280;
    margin-bottom: 2px;
  }

  .info-value {
    font-size: var(--fs-base);
  }

  .text-orange {
    color: main.$color-energy-orange !important;
    font-weight: var(--fw-bold);
  }

  .info-divider.horizontal {
    height: 1px;
    background-color: #e5e7eb;
  }

  .free-time-bar {
    background-color: #fff7ed;
    border-radius: 8px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--fs-sm);
  }

  .free-icon {
    color: #f97316;
  }

  .free-text {
    color: #9a3412;
  }

  .token-section {
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .token-help-text {
    font-size: var(--fs-sm);
    font-weight: var(--fw-medium);
    color: #6b7280;
    text-align: center;
    margin: 0 0 12px;
    line-height: 1.4;
  }

  .token-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .token-input-group {
    display: flex;
    align-items: stretch;
    width: 100%;
    gap: 8px;
  }

  .token-input-field {
    flex: 1;
    min-width: 0;
    height: 40px;
    padding: 0 14px;
    font-size: var(--fs-base);
    font-weight: var(--fw-medium);
    letter-spacing: 0.08em;
    color: #1d1d1f;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: main.$color-energy-orange;
      box-shadow: 0 0 0 3px rgba(245, 138, 72, 0.1);
    }

    &::placeholder {
      color: #aaa;
      letter-spacing: 0;
      font-size: var(--fs-sm);
    }
  }

  .token-submit-btn {
    height: 40px;
    flex-shrink: 0;
  }

  .token-message {
    font-size: var(--fs-xs);
    font-weight: var(--fw-medium);
    text-align: center;
    margin: 10px 0 0;
  }

  .action-btn-modern {
    border-radius: 12px;
    padding: 10px 20px;
    font-weight: var(--fw-semibold);
    font-size: var(--fs-sm);
    letter-spacing: -0.01em;
    transition: all 0.2s ease;
  }

  .observations-list {
    list-style-type: none;
    padding: 0;
    margin: 0;
    text-align: left;
  }

  .observations-list li {
    margin-bottom: 8px;
    font-size: var(--fs-xs);
    display: flex;
    align-items: flex-start;
  }

  .observations-list li .svg-inline--fa {
    margin-top: 2px;
    width: 14px;
  }
  @media (width <= 540px) {
    .modern-price-table {
      padding: 16px;
    }

    .token-submit-btn {
      padding: 0 14px !important;
    }

    .footer-actions .action-btn-modern {
      width: 100%;
      max-width: 100% !important;
    }
  }
</style>
